import type { HyperSession } from '../types/session';
import { supabase } from './supabaseStorage';

interface CreditDeductionResult {
  success: boolean;
  currentCredit?: number;
  error?: string;
}

interface CreditCheckResult {
  success: boolean;
  hasCredits: boolean;
  currentCredits?: number;
  error?: string;
}

/**
 * Check current user's credit balance (Supabase RPC).
 */
export const checkUserCredits = async (
  session: HyperSession,
  requiredCredits: number
): Promise<CreditCheckResult> => {
  try {
    const {
      data: { session: authSession },
    } = await supabase.auth.getSession();
    if (!authSession?.user || authSession.user.id !== session.hy_user_id) {
      return { success: false, hasCredits: false, error: 'Not signed in' };
    }

    const { data, error } = await supabase.rpc('check_user_credits', {
      p_required: requiredCredits,
    });

    if (error) {
      return { success: false, hasCredits: false, error: error.message };
    }

    const row = data as { ok?: boolean; credit?: number; has_enough_credit?: boolean; error?: string } | null;
    if (!row?.ok) {
      return { success: false, hasCredits: false, error: row?.error || 'Credit check failed' };
    }

    return {
      success: true,
      hasCredits: !!row.has_enough_credit,
      currentCredits: typeof row.credit === 'number' ? row.credit : 0,
    };
  } catch (error) {
    return {
      success: false,
      hasCredits: false,
      error: error instanceof Error ? error.message : 'Failed to check credits',
    };
  }
};

/**
 * Deduct credits for the signed-in user (Postgres ledger). Server-side agent flows use service_deduct_credits.
 */
export const deductCredits = async (
  session: HyperSession,
  creditAmount: number
): Promise<CreditDeductionResult> => {
  try {
    const {
      data: { session: authSession },
    } = await supabase.auth.getSession();
    if (!authSession?.user || authSession.user.id !== session.hy_user_id) {
      return { success: false, error: 'Not signed in' };
    }

    const { data, error } = await supabase.rpc('deduct_my_credits', {
      p_amount: creditAmount,
      p_reason: 'client_generation',
      p_ref: null,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    const row = data as { ok?: boolean; error?: string; current_credit?: number } | null;
    if (!row?.ok) {
      const msg =
        row?.error === 'insufficient_credits' ? 'Insufficient credits' : row?.error || 'Deduction failed';
      return { success: false, error: msg };
    }

    return { success: true, currentCredit: row.current_credit };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to deduct credits',
    };
  }
};

export const checkSufficientCredits = async (
  session: HyperSession,
  requiredCredits: number
): Promise<{ sufficient: boolean; currentCredits?: number; error?: string }> => {
  const r = await checkUserCredits(session, requiredCredits);
  return {
    sufficient: r.hasCredits,
    currentCredits: r.currentCredits,
    error: r.error,
  };
};
